import { useState } from 'react';
// 'react-router', not 'react-router-dom', to match the redesign layout files that consume this —
// under vitest the two resolve to separate module instances, so a component that reaches for the
// router through the other one cannot see the MemoryRouter its own test renders.
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/useAuth';
import { hasDaIntelligence } from '../utils/entitlements';

/**
 * The field's accessible name, and — with an ellipsis — its placeholder.
 *
 * Names what the box actually matches. The prototype offers two wordings, "trades, suppliers,
 * suburbs" (that describes searching the MARKETPLACE, which this does not do) and "DAs, suburbs,
 * councils". Both are wrong: this posts to /search, whose query runs against developmentNumber,
 * propertyId, description, applicant and the address fields (street, full, suburb, state,
 * postcode) in MultiCouncilAddressService. Council is a separate FILTER and not one of those
 * fields, so a council name typed in here returns nothing.
 */
export const GLOBAL_SEARCH_LABEL = 'Search addresses, DA numbers, suburbs';
export const GLOBAL_SEARCH_PLACEHOLDER = `${GLOBAL_SEARCH_LABEL}…`;

export interface GlobalSearch {
    /**
     * Whether to offer a search field at all. Search is part of the DA intelligence side of the
     * product, so the box is not offered to a business on the free listing alone — an input that
     * only ever leads to an upsell is worse than no input. Presentation only: the /search route
     * itself is unchanged, and the server is still the only thing that refuses a request.
     */
    canSearch: boolean;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    submitSearch: (event: React.FormEvent<HTMLFormElement>) => void;
}

/**
 * Everything a global search field does, in one place.
 *
 * There are two of these fields — the topbar's, from `md:` up, and the dashboard's, below `md:`,
 * where the design moves it off the bar to make room for the logo. They must submit to the same
 * place, trim the same way, do nothing on the same blank submit and be gated on the same
 * entitlement; two copies of that would drift. Each call site keeps its own `searchTerm`, which is
 * correct — they are two separate inputs and only one of them is on screen at a time.
 */
export function useGlobalSearch(): GlobalSearch {
    const { activeBusiness } = useAuth();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');

    /**
     * Hand the typed text to the search page. Call sites use a <form> rather than an onKeyDown so
     * the browser's own Enter-submits-a-form behaviour and a mobile keyboard's "Search" key both
     * work.
     *
     * The text is left in the box on purpose: the user can see what they searched for, and amend
     * and resubmit it. Resubmitting the same text still re-runs the search — Search.tsx watches
     * the navigation, not only the value of `q`.
     */
    const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const query = searchTerm.trim();
        // Nothing to search for. Navigating to a bare /search would also throw away whatever the
        // user already had on that page, which is worse than doing nothing.
        if (!query) return;
        navigate(`/search?q=${encodeURIComponent(query)}`);
    };

    return {
        canSearch: hasDaIntelligence(activeBusiness),
        searchTerm,
        setSearchTerm,
        submitSearch,
    };
}
